import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import {
  LayoutDashboard, Stethoscope, Building2, Pill, MapPin, ShoppingCart,
  Wallet, Boxes, Activity, Receipt, TrendingUp, Users, Languages, LogOut, ChevronRight,
  MessageCircle, Map, Route, ScrollText, Sun, Moon,
  FileText, FileSignature, Users2, Settings, CalendarCheck, ClipboardList,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import NotificationBell from "@/components/NotificationBell";

const navConfig = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, perm: "dashboard.view" },
  { to: "/plan-dashboard", key: "planDashboard", icon: CalendarCheck, perm: "plans.view_own" },
  { to: "/plans", key: "weeklyPlans", icon: ClipboardList, perm: "plans.view_own" },
  { to: "/doctors", key: "doctors", icon: Stethoscope, perm: "doctors.view" },
  { to: "/pharmacies", key: "pharmacies", icon: Building2, perm: "pharmacies.view" },
  { to: "/customers", key: "customers", icon: Users2, perm: "customers.view" },
  { to: "/products", key: "products", icon: Pill, perm: "products.view" },
  { to: "/map", key: "mapView", icon: Map, perm: "map.view" },
  { to: "/routes", key: "routePlanner", icon: Route, perm: "routes.manage" },
  { to: "/visits", key: "visits", icon: MapPin, perm: "visits.view_own" },
  { to: "/quotations", key: "quotations", icon: FileSignature, perm: "quotations.view_own" },
  { to: "/invoices", key: "invoices", icon: FileText, perm: "invoices.view_own" },
  { to: "/orders", key: "orders", icon: ShoppingCart, perm: "orders.view_own" },
  { to: "/collections", key: "collections", icon: Wallet, perm: "collections.view_own" },
  { to: "/whatsapp", key: "whatsapp", icon: MessageCircle, perm: "whatsapp.view" },
  { to: "/inventory", key: "inventory", icon: Boxes, perm: "inventory.view" },
  { to: "/market-intel", key: "marketIntel", icon: Activity, perm: "market_intel.view" },
  { to: "/expenses", key: "expenses", icon: Receipt, perm: "expenses.view" },
  { to: "/cashflow", key: "cashflow", icon: TrendingUp, perm: "cashflow.view" },
  { to: "/performance", key: "performance", icon: TrendingUp, perm: "performance.view" },
  { to: "/audit", key: "auditLog", icon: ScrollText, perm: "audit.view" },
  { to: "/users", key: "users", icon: Users, perm: "users.view" },
  { to: "/settings/company", key: "companySettings", icon: Settings, perm: "settings.company.view" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, toggle, isRTL } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();

  const visibleNav = navConfig.filter((n) => !n.perm || user?.permissions?.includes(n.perm));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex" dir={isRTL ? "rtl" : "ltr"}>
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col bg-white dark:bg-slate-900 border-e border-slate-200 dark:border-slate-800 sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-blue-700 grid place-items-center text-white font-extrabold tracking-tighter shadow-sm">
              Px
            </div>
            <div>
              <div className="font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "Manrope" }}>
                {t("appName")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 font-bold">
                {t("appTagline")}
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border-s-4 border-blue-700 dark:border-blue-400 ps-2"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                }`
              }
            >
              <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              <span className="truncate">{t(item.key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 dark:border-slate-800 p-3 space-y-2">
          <div className="flex gap-2">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Languages className="w-4 h-4" />
              <span>{lang === "en" ? "AR" : "EN"}</span>
            </button>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {theme === "dark" ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>}
              <span>{theme === "dark" ? "Light" : "Dark"}</span>
            </button>
          </div>
          <div className="px-3 py-2">
            <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 capitalize">{t(user?.role)}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
          >
            <LogOut className="w-4 h-4" />
            <span>{t("logout")}</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-blue-700 grid place-items-center text-white font-extrabold text-sm">
            Px
          </div>
          <span className="font-extrabold text-slate-900 dark:text-white">{t("appName")}</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell/>
          <button onClick={toggle} className="text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200">
            {lang === "en" ? "AR" : "EN"}
          </button>
          <button onClick={handleLogout} className="text-red-600">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid grid-cols-5 gap-0">
        {visibleNav.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2 text-[10px] ${
                isActive ? "text-blue-700" : "text-slate-500"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="truncate">{t(item.key)}</span>
          </NavLink>
        ))}
      </div>

      <main className="flex-1 min-w-0 lg:pt-0 pt-14 pb-20 lg:pb-0">
        {/* Desktop top bar with notification bell */}
        <div className="hidden lg:flex items-center justify-end px-10 pt-4 -mb-2">
          <NotificationBell/>
        </div>
        <div className="page-enter px-4 sm:px-6 lg:px-10 py-6 max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
